import React from 'react'
import ReactDOM from 'react-dom'

export function AlertDialog({ children, open, onOpenChange }) {
  if (!open) return null;

  // Use React Portal to render outside of parent Dialog's DOM tree
  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[200] bg-black/50 flex items-center justify-center"
      style={{ pointerEvents: 'auto' }}
      onClick={(e) => {
        // Only close if clicking the backdrop itself, not its children
        if (e.target === e.currentTarget) {
          onOpenChange?.(false)
        }
      }}
    >
      <div className="relative z-[201]" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>,
    document.body
  )
}

export function AlertDialogTrigger({ children, onClick, ...props }) {
  return React.cloneElement(children, {
    onClick: (e) => {
      children.props.onClick?.(e)
      onClick?.(e)
    },
    ...props
  })
}

export function AlertDialogContent({ children }) {
  return (
    <div className="w-[calc(100vw-2rem)] max-w-[480px] p-6 sm:p-8 bg-white rounded-2xl shadow-[0px_4px_6px_-4px_rgba(0,0,0,0.10)] shadow-lg outline outline-1 outline-offset-[-1px] outline-neutral-200 inline-flex flex-col justify-start items-start gap-2">
      <div className="self-stretch flex flex-col justify-start items-start gap-4">
        {children}
      </div>
    </div>
  )
}

export function AlertDialogHeader({ children }) {
  return (
    <div className="self-stretch justify-start text-neutral-900 text-xl font-semibold leading-normal">
      {children}
    </div>
  )
}

export function AlertDialogTitle({ children }) {
  return children
}

export function AlertDialogDescription({ children }) {
  return (
    <div className="self-stretch justify-start text-neutral-500 text-sm font-normal leading-tight tracking-tight">
      {children}
    </div>
  )
}

export function AlertDialogFooter({ children }) {
  return (
    <div className="self-stretch flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
      {children}
    </div>
  )
}

export function AlertDialogCancel({ children, onClick, ...props }) {
  return (
    <button
      onClick={onClick}
      className="min-h-9 px-4 py-2 rounded-lg shadow-[0px_1px_2px_-1px_rgba(0,0,0,0.10)] shadow-[0px_1px_3px_0px_rgba(0,0,0,0.10)] outline outline-1 outline-neutral-200 flex justify-center items-center gap-2 hover:bg-gray-50 transition-colors"
      {...props}
    >
      <div className="text-center justify-center text-neutral-900 text-sm font-medium leading-tight tracking-tight">
        {children}
      </div>
    </button>
  )
}

export function AlertDialogAction({ children, onClick, variant, disabled, ...props }) {
  // Handle destructive variant for delete actions
  const isDestructive = variant === 'destructive'

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={
        isDestructive
          ? `min-h-9 px-4 py-2 bg-red-600 rounded-lg flex justify-center items-center gap-2 hover:bg-red-700 transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`
          : `min-h-9 px-4 py-2 bg-green-600 rounded-lg flex justify-center items-center gap-2 hover:bg-green-700 transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`
      }
      {...props}
    >
      <div className="text-center justify-center text-white text-sm font-medium leading-tight tracking-tight">
        {children}
      </div>
    </button>
  )
}